import { useChildMatches } from "@tanstack/react-router";
import { createContext, useContext } from "react";

/**
 * Creates a context factory that generates a React Context Provider and a custom hook to access the context.
 *
 * This utility helps streamline the creation and consumption of React contexts by providing
 * proper TypeScript typing and automatic error handling when the context is accessed outside its provider.
 *
 * @template ContextData - The type of the data stored in the context
 * @param options - Configuration options for the context
 * @param options.defaultValue - The default value for the context (defaults to null)
 * @param options.errorMessage - Custom error message when context is used outside provider (defaults to "useContext must be used within a Provider")
 * @returns A tuple containing [ContextProvider, useContext] where:
 *   - ContextProvider: React Context.Provider component
 *   - useContext: Custom hook that returns the context value with proper typing
 *
 * @example
 * ```tsx
 * // Creating a context
 * const [UserProvider, useUser] = createContextFactory<{ username: string }>();
 *
 * // Using the provider
 * function App() {
 *   return (
 *     <UserProvider value={{ username: 'john' }}>
 *       <UserProfile />
 *     </UserProvider>
 *   );
 * }
 *
 * // Consuming the context
 * function UserProfile() {
 *   const { username } = useUser();
 *   return <h1>Hello, {username}</h1>;
 * }
 * ```
 */
export function createContextFactory<ContextData>(options?: {
  defaultValue?: ContextData | null;
  errorMessage?: string;
}) {
  const opts = {
    defaultValue: null,
    errorMessage: "useContext must be used within a Provider",
    ...options,
  };

  const context = createContext<ContextData | null>(opts.defaultValue);

  function useContextFactory(): ContextData {
    const contextValue = useContext(context);
    if (contextValue === null) {
      throw new Error(opts.errorMessage);
    }
    return contextValue;
  }

  return [context.Provider, useContextFactory] as const;
}

export function useRouteFeatures() {
  return useChildMatches({
    select: (matches) =>
      matches.reduce((features, match) => {
        for (const feature of match.context.features) {
          feature.startsWith("!")
            ? features.delete(feature.slice(1))
            : features.add(feature);
        }
        return features;
      }, new Set<string>()),
  });
}
